import { Injectable } from '@angular/core';
import { FormControl, FormBuilder, Validators, FormGroup, MinLengthValidator } from '@angular/forms';
import { FormField } from './form-field';
import { maxLengthInBytes, notNullOrWhiteSpace, dateValidation } from '../../../shared/validators';

@Injectable()
export class FormQuestionService {

    private regexPattern = '[ A-Za-z0-9]+';
    private numericPattern = '[- +()0-9]+';
    private dateFormat = '[0-9]{2}-[a-zA-z]{3}-[0-9]{4}$';

    constructor(
        private fb: FormBuilder) {

    }

    public getAllFormFields() {
        const formFields: FormField[] = [
            new FormField(1, 'containerBarcode' , '' , null, true),
            new FormField(2, 'shippingFrom'),
            new FormField(3, 'shippingToLabcorp'),
            new FormField(4, 'destination', ),
            new FormField(5, 'airBillNumber'),
            new FormField(6, 'courier'),
            new FormField(7, 'scheduledShipmentDate'),
        ];
        var fromLocation = formFields.filter(t => t.sequence === 2)[0];
        fromLocation.relatedFormFields = this.addAddressFields(2, 'shipping', 'shippingFromAddress');

        var toLocation = formFields.filter(t => t.sequence === 4)[0];
        toLocation.relatedFormFields = this.addAddressFields(4, 'destination', 'destinationAddress');
        return formFields;
    }

    public getShipmentFormGroup(data?: any) {
        let shippingFromAddress = this.loadShippingAddressControls(data ? data.shippingFromAddress : data);
        let destinationAddress = this.loadDestinationAddressControls(data ? data.destinationAddress : data);

        return this.loadShippingFormControls(data, shippingFromAddress, destinationAddress);
    }

    public loadInProgressFormData(data: any) {
        let shippingFromAddress = this.loadShippingAddressControls(data);
        let destinationAddress = this.loadDestinationAddressControls(data);
        let shippingAddress = this.getAddressData(data);
        let destination = this.getDestinationAddressData(data);
        let shipmentFormGroup = this.loadShippingFormControls(data, shippingFromAddress, destinationAddress);
        shipmentFormGroup.patchValue({ 'shippingFrom': shippingAddress, 'destination': destination });
        return shipmentFormGroup;
    }

    private addAddressFields(sequence: number, key: string, groupName: string) {
        return [
            new FormField(sequence, key + 'OrgName', groupName, 1),
            new FormField(sequence, key + 'AddressLine1', groupName, 2),
            new FormField(sequence, key + 'AddressLine2', groupName, 3),
            new FormField(sequence, key + 'City', groupName, 4),
            new FormField(sequence, key + 'State', groupName, 5),
            new FormField(sequence, key + 'PostalCode', groupName, 6),
            new FormField(sequence, key + 'Country', groupName, 7),
            new FormField(sequence, key + 'OrgPhone', groupName, 8),
        ];
    }

    private loadShippingAddressControls(data: any) {
        return this.fb.group({
            'shippingOrgName': new FormControl(data ? data.shippingOrgName : '', [maxLengthInBytes(30)]),
            'shippingAddressLine1': new FormControl(data ? data.shippingAddressLine1 : '', [Validators.required, notNullOrWhiteSpace(), maxLengthInBytes(30)]),
            'shippingAddressLine2': new FormControl(data ? data.shippingAddressLine2 : '', maxLengthInBytes(30)),
            'shippingCity': new FormControl(data ? data.shippingCity : '', [maxLengthInBytes(30)]),
            'shippingState': new FormControl(data ? data.shippingState : '', [Validators.pattern(this.regexPattern), maxLengthInBytes(2)]),
            'shippingPostalCode': new FormControl(data ? data.shippingPostalCode : '', [maxLengthInBytes(20)]),
            'shippingCountry': new FormControl(data ? data.shippingCountry : '', [Validators.required, notNullOrWhiteSpace(), Validators.pattern(this.regexPattern), maxLengthInBytes(30)]),
            'shippingOrgPhone': new FormControl(data ? data.shippingOrgPhone : '', [maxLengthInBytes(20), Validators.pattern(this.numericPattern)]),
        });
    }

    private loadDestinationAddressControls(data: any) {
        return this.fb.group({
            'destinationOrgName': new FormControl(data ? data.destinationOrgName : '', [Validators.required, notNullOrWhiteSpace(), maxLengthInBytes(30)]),
            'destinationAddressLine1': new FormControl(data ? data.destinationAddressLine1 : '', [Validators.required, notNullOrWhiteSpace(), maxLengthInBytes(30)]),
            'destinationAddressLine2': new FormControl(data ? data.destinationAddressLine2 : '', maxLengthInBytes(30)),
            'destinationCity': new FormControl(data ? data.destinationCity : '', [maxLengthInBytes(30)]),
            'destinationState': new FormControl(data ? data.destinationState : '', [Validators.pattern(this.regexPattern), maxLengthInBytes(2)]),
            'destinationPostalCode': new FormControl(data ? data.destinationPostalCode : '', [maxLengthInBytes(20)]),
            'destinationCountry': new FormControl(data ? data.destinationCountry : '', [Validators.required, notNullOrWhiteSpace(), Validators.pattern(this.regexPattern), maxLengthInBytes(30)]),
            'destinationOrgPhone': new FormControl(data ? data.destinationOrgPhone : '', [maxLengthInBytes(20), Validators.pattern(this.numericPattern)]),
        });
    }

    private loadShippingFormControls(data: any, shippingFromAddress: FormGroup, destinationAddress: FormGroup) {
        return this.fb.group({
            'containerBarcode': new FormControl(data ? data.containerBarcode : ''),
            'shippingFrom': new FormControl(data ? data.shippingFrom : '', [Validators.required]),
            'shippingFromAddress': shippingFromAddress,
            'shippingToLabcorp': new FormControl(data ? data.shippingToLabcorp : 'yes'),
            'destination': new FormControl(data ? data.destination : '', [Validators.required]),
            'destinationAddress': destinationAddress,
            'airBillNumber': new FormControl(data ? data.airBillNumber : '', [Validators.required, maxLengthInBytes(30), notNullOrWhiteSpace(), Validators.minLength(9)]),
            'courier': new FormControl(data ? data.courier : '', [Validators.required, notNullOrWhiteSpace(), maxLengthInBytes(100)]),
            'scheduledShipmentDate': new FormControl(data ? data.scheduledShipmentDate : '', [Validators.required, notNullOrWhiteSpace(), dateValidation(), Validators.pattern(this.dateFormat)]),
        });
    }

    private getAddressData(data: any) {
        let value = '';
        if (data.shippingOrgName) {
            value = value + data.shippingOrgName + ', ';
        }
        if (data.shippingAddressLine1) {
            value = value + data.shippingAddressLine1 + ', ';
        }
        if (data.shippingAddressLine2) {
            value = value + data.shippingAddressLine2 + ', ';
        }
        if (data.shippingCity) {
            value = value + data.shippingCity + ', ';
        }
        if (data.shippingState) {
            value = value + data.shippingState + ', ';
        }
        if (data.shippingCountry) {
            value = value + data.shippingCountry + ', ';
        }
        if (data.shippingPostalCode) {
            value = value + data.shippingPostalCode;
        }
        return value;
    }

    private getDestinationAddressData(data: any) {
        if (data.shippingToLabcorp && data.shippingToLabcorp === 'no') {
            return 'Others';
        }
        if (data.shippingToLabcorp && data.shippingToLabcorp === 'yes') {
            return  data.destinationOrgName + ' - Preferred';
        }
        return '';
    }
}